/**
 * URL Parameter utilities for search pages
 * Allows encoding search state to URL and decoding URL to search state
 */

import { PageName } from '../types';

/**
 * Get the page name from URL params
 */
export const getPageFromURL = (): PageName | null => {
  const params = new URLSearchParams(window.location.search);
  const page = params.get('p');
  if (page) {
    return page as PageName;
  }
  return null;
};

/**
 * Parse a comma-separated string into an array, filtering empty values
 */
export const parseArrayParam = (value: string | null): string[] => {
  if (!value) return [];
  return value.split(',').filter((v) => v.trim() !== '');
};

/**
 * Encode an array as a comma-separated string, or undefined if empty
 */
export const encodeArrayParam = (arr: string[]): string | undefined => {
  if (!arr || arr.length === 0) return undefined;
  return arr.join(',');
};

/**
 * Get all URL search params as an object
 */
export const getURLParams = (): URLSearchParams => {
  return new URLSearchParams(window.location.search);
};

/**
 * Update URL with new params without page reload
 * Only includes non-empty values to keep URLs clean
 */
export const updateURLParams = (params: Record<string, string | number | string[] | undefined>): void => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;

    if (Array.isArray(value)) {
      if (value.length > 0) {
        searchParams.set(key, value.join(','));
      }
    } else {
      searchParams.set(key, String(value));
    }
  });

  const queryString = searchParams.toString();
  const newURL = queryString
    ? `${window.location.pathname}?${queryString}`
    : window.location.pathname;

  window.history.replaceState({}, '', newURL);
};

/**
 * Clear all URL params
 */
export const clearURLParams = (): void => {
  window.history.replaceState({}, '', window.location.pathname);
};

/**
 * Check if URL has any search params
 */
export const hasURLParams = (): boolean => {
  return window.location.search.length > 1;
};

// ============ MMC Search Params ============

export interface MMCURLParams {
  make?: string;
  model?: string;
  colors?: string[];
  colorFilterMode?: string;  // 'any' | 'none' | 'notEmpty' | 'specific'
  colorChecked?: string;     // 'any' | 'checked' | 'unchecked'
  body?: string;
  type?: string;
  datasets?: string[];
  year?: string;
  vin?: string;
  tag?: string;
  id?: string;
  mustHave?: string[];
  atLeastOne?: string[];
  dontHave?: string[];
  sort?: string;
  pageSize?: number;
  page?: number;
}

export const decodeMMCParams = (): MMCURLParams => {
  const params = getURLParams();
  return {
    make: params.get('make') || undefined,
    model: params.get('model') || undefined,
    colors: parseArrayParam(params.get('colors')),
    colorFilterMode: params.get('colorFilterMode') || undefined,
    colorChecked: params.get('colorChecked') || undefined,
    body: params.get('body') || undefined,
    type: params.get('type') || undefined,
    datasets: parseArrayParam(params.get('datasets')),
    year: params.get('year') || undefined,
    vin: params.get('vin') || undefined,
    tag: params.get('tag') || undefined,
    id: params.get('id') || undefined,
    mustHave: parseArrayParam(params.get('mustHave')),
    atLeastOne: parseArrayParam(params.get('atLeastOne')),
    dontHave: parseArrayParam(params.get('dontHave')),
    sort: params.get('sort') || undefined,
    pageSize: params.get('pageSize') ? parseInt(params.get('pageSize')!, 10) : undefined,
    page: params.get('page') ? parseInt(params.get('page')!, 10) : undefined,
  };
};

export const encodeMMCParams = (params: MMCURLParams): void => {
  updateURLParams({
    p: 'searchMmc', // Page identifier
    make: params.make,
    model: params.model,
    colors: params.colors,
    colorFilterMode: params.colorFilterMode !== 'any' ? params.colorFilterMode : undefined, // Only encode non-default
    colorChecked: params.colorChecked !== 'any' ? params.colorChecked : undefined, // Only encode non-default
    body: params.body,
    type: params.type,
    datasets: params.datasets,
    year: params.year,
    vin: params.vin,
    tag: params.tag,
    id: params.id,
    mustHave: params.mustHave,
    atLeastOne: params.atLeastOne,
    dontHave: params.dontHave,
    sort: params.sort !== 'normal' ? params.sort : undefined, // Only encode non-default
    pageSize: params.pageSize !== 20 ? params.pageSize : undefined, // Only encode non-default
    page: params.page !== 1 ? params.page : undefined, // Only encode non-default
  });
};

// ============ Plate Search Params ============

export interface PlateURLParams {
  dataset?: string;
  region?: string;
  flags?: string[];
  tag?: string;
  id?: string;
  sort?: string;
  pageSize?: number;
  page?: number;
}

export const decodePlateParams = (): PlateURLParams => {
  const params = getURLParams();
  return {
    dataset: params.get('dataset') || undefined,
    region: params.get('region') || undefined,
    flags: parseArrayParam(params.get('flags')),
    tag: params.get('tag') || undefined,
    id: params.get('id') || undefined,
    sort: params.get('sort') || undefined,
    pageSize: params.get('pageSize') ? parseInt(params.get('pageSize')!, 10) : undefined,
    page: params.get('page') ? parseInt(params.get('page')!, 10) : undefined,
  };
};

export const encodePlateParams = (params: PlateURLParams): void => {
  updateURLParams({
    p: 'searchPlate', // Page identifier
    dataset: params.dataset,
    region: params.region,
    flags: params.flags,
    tag: params.tag,
    id: params.id,
    sort: params.sort !== 'normal' ? params.sort : undefined,
    pageSize: params.pageSize !== 20 ? params.pageSize : undefined,
    page: params.page !== 1 ? params.page : undefined,
  });
};

// ============ Detection Search Params ============

export interface DetectionURLParams {
  datasets?: string[];
  mustHave?: string[];
  atLeastOne?: string[];
  dontHave?: string[];
  desc?: string;
  id?: string;
  sort?: string;
  pageSize?: number;
  page?: number;
}

export const decodeDetectionParams = (): DetectionURLParams => {
  const params = getURLParams();
  return {
    datasets: parseArrayParam(params.get('datasets')),
    mustHave: parseArrayParam(params.get('mustHave')),
    atLeastOne: parseArrayParam(params.get('atLeastOne')),
    dontHave: parseArrayParam(params.get('dontHave')),
    desc: params.get('desc') || undefined,
    id: params.get('id') || undefined,
    sort: params.get('sort') || undefined,
    pageSize: params.get('pageSize') ? parseInt(params.get('pageSize')!, 10) : undefined,
    page: params.get('page') ? parseInt(params.get('page')!, 10) : undefined,
  };
};

export const encodeDetectionParams = (params: DetectionURLParams): void => {
  updateURLParams({
    p: 'searchDet', // Page identifier
    datasets: params.datasets,
    mustHave: params.mustHave,
    atLeastOne: params.atLeastOne,
    dontHave: params.dontHave,
    desc: params.desc,
    id: params.id,
    sort: params.sort !== 'normal' ? params.sort : undefined,
    pageSize: params.pageSize !== 20 ? params.pageSize : undefined,
    page: params.page !== 1 ? params.page : undefined,
  });
};
